/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {currencyNumberFormat} from "utils/utility";
import {Package} from "types/entities/Package";
import {PersonType} from "types/enums/PersonType";
import {useTranslation} from "react-i18next";
import {PackageItem} from "types/entities/PackageItem";
import {PackageCategory} from "types/enums/PackageCategory";
import {getExtraHeatPackageItem, isGiftCard, uniquePackageWithActivityItem} from "utils/packageUtils";
import PackageTag from "pages/Front/Booking/components/PackageTag";
import PackageMinDaysFromNow from "pages/Front/Booking/components/Packages/PackageMinDaysFromNow";
import {SHOW_BOOKABLE_DAYS_FROM_NOW, SHOW_STANDARD_PRICE} from "helpers/config";

interface PackageDetailProps {
    packageDetail: Package;
}

const SumReducer = (accumulator, cur: PackageItem) => {
    if (isNaN(accumulator)) {
        return cur.price;
    }
    return accumulator + cur.price;
};

interface PackageLabelValueProps {
    label: string;
    value: string;
    packageDetail?: Package;
}

interface PricingSchema {
    length: number;
    prices: number[];
}

const PackageDetailStandardPriceTag = (props: PackageLabelValueProps) => {
    return (
        <div className={"flex flex-row justify-center h-5 mt-2 -mb-5 z-40"}>
            <div className={"absolute z-40"}>
                {props.packageDetail && <PackageTag key={props.packageDetail.id} className={"bg-gray-400"} label={props.label} />}
            </div>
        </div>
    );
};
const PackageDetailStandardPrice = (props: PackageLabelValueProps) => {
    return (
        <span className={"text-base text-gray-900 flex flex-row justify-between max-w-lg"}>
            <dd className={"text-base"}>{props.label}</dd>
            <dt className={"inline-flex items-end"}>
                <em>
                    <span>{props.value}</span>
                </em>
            </dt>
        </span>
    );
};
const PackageDetailInfoValues = (props: PackageDetailProps) => {
    const {t} = useTranslation();
    const adultFilteredPackages = uniquePackageWithActivityItem(props.packageDetail, true, PersonType.Adult);
    const childFilteredPackages = uniquePackageWithActivityItem(props.packageDetail, true, PersonType.Child);
    const extraHeatPackageItem = getExtraHeatPackageItem(props.packageDetail);
    // @ts-ignore
    const adultsPrice = adultFilteredPackages.includes(undefined) ? 0 : adultFilteredPackages.reduce(SumReducer, 0);
    // @ts-ignore
    const childrenPrice = childFilteredPackages.includes(undefined) ? 0 : childFilteredPackages.reduce(SumReducer, 0);
    const packageDetailPriceLabel = () => {
        if (props.packageDetail.extra.bookableByHour == 1) {
            return t("Price per hour from");
        }

        if (props.packageDetail.rewrite_url === "exclusive") {
            return t("Price per hour from");
        }
        if (props.packageDetail.min_drivers_count <= 10) {
            return t("Price from");
        }
        return t("price from number persons", {number: props.packageDetail.min_drivers_count});
    };
    const priceMidweek = props.packageDetail?.packageItemsPrices?.midweek;
    const priceMidweekChild = props.packageDetail?.packageItemsPrices?.midweekChild;
    const priceWeekend = props.packageDetail?.packageItemsPrices?.weekend;
    const priceWeekendChild = props.packageDetail?.packageItemsPrices?.weekendChild;

    return (
        <div className={"PackageDetailInfoValues flex-1 flex flex-col text-sm leading-4 px-2 py-4 justify-start space-y-2 sm:space-y-6 text-gray-700"}>
            {!SHOW_STANDARD_PRICE && props.packageDetail?.packageItemsPrices && (
                <div>
                    <PackageDetailStandardPrice label={t("Monday till Thursday")} value={priceMidweek ? currencyNumberFormat(priceMidweek) : "----"} />
                    <PackageDetailStandardPrice label={t("Friday till Sunday")} value={priceWeekend ? currencyNumberFormat(priceWeekend) : "----"} />
                </div>
            )}
            {SHOW_STANDARD_PRICE && (
                <div>
                    {props.packageDetail.category === PackageCategory.Event && (
                        <div>
                            <PackageDetailStandardPrice label={packageDetailPriceLabel()} value={currencyNumberFormat(props.packageDetail.price)} />
                            {adultsPrice > 0 && <PackageDetailStandardPrice label={t("Extra person")} value={currencyNumberFormat(adultsPrice)} />}
                            {childrenPrice !== adultsPrice && childrenPrice > 0 && (
                                <PackageDetailStandardPrice label={t("Extra person child")} value={currencyNumberFormat(childrenPrice)} />
                            )}
                            {extraHeatPackageItem && (
                                <PackageDetailStandardPrice label={t("Extra race")} value={currencyNumberFormat(extraHeatPackageItem.price)} />
                            )}
                        </div>
                    )}

                    {props.packageDetail.category !== PackageCategory.Event && (
                        <div>
                            {adultsPrice > 0 && <PackageDetailStandardPrice label={t("Adult price")} value={currencyNumberFormat(adultsPrice)} />}

                            {childrenPrice > 0 && <PackageDetailStandardPrice label={t("Children price")} value={currencyNumberFormat(childrenPrice)} />}
                            {!isGiftCard(props.packageDetail) && (
                                <PackageDetailStandardPriceTag label={t("Standard prices")} packageDetail={props.packageDetail} value={""} />
                            )}
                        </div>
                    )}
                </div>
            )}

            <hr className={"border-t border-gray-500"} />
            {SHOW_BOOKABLE_DAYS_FROM_NOW && <PackageMinDaysFromNow package={props.packageDetail} />}
            {/*{(DEFAULT_HALL !== HallCode.Winterthur && DEFAULT_HALL !== HallCode.Spreitenbach) && (*/}
            {/*  <>*/}
            {/*      <span className={""}>*/}
            {/*          {t("This package is not recommended for groups beyond")}*/}
            {/*        &nbsp;<b>{props.packageDetail.max_drivers_count}</b> {t("persons")}*/}
            {/*      </span>*/}
            {/*    <span>*/}
            {/*          {t("Valid on selected date or")}&nbsp;*/}
            {/*      <b>{props.packageDetail.valid_days_from_purchase}</b> {t("days")}*/}
            {/*      </span>*/}

            {/*    <span>{t("Complete terms and conditions of each package are available here")}</span>*/}
            {/*  </>*/}
            {/*)}*/}
        </div>
    );
};

export default PackageDetailInfoValues;
